import { clamp, distanceSq, IPoint } from 'framework/utils/math-utils';
import { PinchCatcher } from 'js/crossword/input/PinchCatcher';
import OS from 'framework/utils/OS';

export interface IBoardTransform {
  width: number;
  height: number;
  pinching: boolean;
  moved: boolean;
  zoom: number;
  offsetX: number;
  offsetY: number;
  centerOffsetX: number;
}

export class BoardInputHandler {
  private transform: IBoardTransform;
  private pinchCatcher: PinchCatcher | null = null;
  private isDown = false;
  private clickPoint = { x: 0, y: 0 };
  private container: HTMLElement;

  constructor(container: HTMLElement, transform: IBoardTransform) {
    this.transform = transform;
    this.container = container;
    OS.desktop ? this.initDesktop(container) : this.initMobile(container);
  }

  private initMobile(container: HTMLElement) {
    this.initPintchCatcher(container);

    container.addEventListener('touchstart', this.startMoveHandler);
    window.addEventListener('touchend', this.endMoveHandler);

    container.addEventListener('touchmove', (ev) => {
      if (this.transform.pinching) {
        ev.preventDefault();
        return;
      }
      if (!this.isDown) return;
      if (this.transform.zoom === 1) return;

      const pageX = ev.touches[0].screenX;
      const pageY = ev.touches[0].screenY;

      const deltaX = -(this.clickPoint.x - pageX) / this.transform.zoom;
      let deltaY = -(this.clickPoint.y - pageY) / this.transform.zoom;
      // it means that scroll in progress
      if (!ev.cancelable) deltaY = 0;

      if (this.shouldPreventMoveDefault(deltaX, deltaY)) {
        ev.preventDefault();
      }

      this.changeOffset(deltaX, deltaY);

      if (!this.transform.moved && distanceSq(this.clickPoint, { x: pageX, y: pageY }) > 10) {
        this.transform.moved = true;
      }

      this.clickPoint.x = pageX;
      this.clickPoint.y = pageY;
    });
  }

  private initDesktop(container: HTMLElement) {
    container.addEventListener('pointerdown', this.startMoveHandler);
    window.addEventListener('pointerup', this.endMoveHandler);
    container.addEventListener('pointermove', (ev) => {
      if (!this.isDown) return;
      if (this.transform.zoom === 1) return;
      const deltaX = -(this.clickPoint.x - ev.pageX) / this.transform.zoom;
      const deltaY = -(this.clickPoint.y - ev.pageY) / this.transform.zoom;
      this.changeOffset(deltaX, deltaY);

      if (!this.transform.moved && distanceSq(this.clickPoint, { x: ev.pageX, y: ev.pageY }) > 10) {
        this.transform.moved = true;
      }

      this.clickPoint.x = ev.pageX;
      this.clickPoint.y = ev.pageY;
    });

    container.addEventListener('wheel', (ev) => {
      const deltaAdjusted = Math.abs(ev.deltaY) < 50 ? Math.sign(ev.deltaY) * 50 : ev.deltaY;
      this.changeZoom(-deltaAdjusted / 1000);
      ev.preventDefault();
    });
  }

  private startMoveHandler = (ev: PointerEvent | TouchEvent) => {
    if (this.isDown) return;
    this.isDown = true;
    if (ev instanceof PointerEvent) {
      this.clickPoint.x = ev.pageX;
      this.clickPoint.y = ev.pageY;
    } else {
      this.clickPoint.x = ev.touches[0].screenX;
      this.clickPoint.y = ev.touches[0].screenY;
    }
  };

  private endMoveHandler = () => {
    if (this.transform.moved) {
      setTimeout(() => {
        this.transform.moved = false;
      }, 1);
    }
    this.isDown = false;
  };

  private initPintchCatcher(container: HTMLElement): void {
    const pinchCatcher = new PinchCatcher(container);
    pinchCatcher.onPinch.add((delta) => {
      this.changeZoom(delta * 0.005);
    });

    pinchCatcher.onPinchStarted.add(() => {
      this.transform.pinching = true;
    });
    pinchCatcher.onPinchStopped.add(() => {
      this.transform.pinching = false;
    });
  }

  public changeZoom(delta: number): void {
    this.transform.zoom = clamp(this.transform.zoom + delta, 1, 2);
    this.changeOffset(0, 0);
  }

  private changeOffset(deltaX: number, deltaY: number) {
    this.setOffset(this.transform.offsetX + deltaX, this.transform.offsetY + deltaY);
  }

  public setOffset(x: number, y: number) {
    const { x: maxOffsetX, y: maxOffsetY } = this.getMaxOffset();
    this.transform.offsetX = clamp(x, -maxOffsetX, maxOffsetX);
    this.transform.offsetY = clamp(y, -maxOffsetY, maxOffsetY);
  }

  private getMaxOffset(): IPoint {
    const { width, height } = this.transform;
    return {
      x: (width - width / this.transform.zoom) * 0.5,
      y: (height - height / this.transform.zoom) * 0.5,
    };
  }

  private shouldPreventMoveDefault(deltaX: number, deltaY: number): boolean {
    const { x: maxOffsetX, y: maxOffsetY } = this.getMaxOffset();
    const newY = this.transform.offsetY + deltaY;
    return newY < maxOffsetY && newY > -maxOffsetY;
  }
}
